import React from "react";

/**
 * NetworkVisual — lightweight SVG (works on every browser).
 * Concentric dotted rings + 8 outer nodes + connection lines + central coral pulse.
 * No WebGL, no Canvas — pure SVG + CSS.
 */
export const NetworkVisual = ({ className = "", color = "#BEE5E0", accent = "#FF6857" }) => {
  const CX = 300;
  const CY = 300;
  // 8 outer nodes evenly distributed around the outer ring
  const outerNodes = Array.from({ length: 8 }).map((_, i) => {
    const angle = (i / 8) * Math.PI * 2 - Math.PI / 2;
    const r = 260;
    return {
      x: CX + r * Math.cos(angle),
      y: CY + r * Math.sin(angle),
      delay: i * 0.18,
    };
  });
  // 5 mid-ring nodes for visual depth
  const midNodes = Array.from({ length: 5 }).map((_, i) => {
    const angle = (i / 5) * Math.PI * 2 + Math.PI / 6;
    const r = 170;
    return { x: CX + r * Math.cos(angle), y: CY + r * Math.sin(angle), delay: 0.4 + i * 0.18 };
  });

  return (
    <svg
      viewBox="0 0 600 600"
      className={`network-visual ${className}`}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="nv-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={accent} stopOpacity="0.32" />
          <stop offset="60%" stopColor={accent} stopOpacity="0.05" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="nv-bgglow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="70%" stopColor={color} stopOpacity="0.04" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Background glow */}
      <circle cx={CX} cy={CY} r={300} fill="url(#nv-bgglow)" />

      {/* Slow-rotating dotted rings */}
      <g className="nv-rings" style={{ transformOrigin: "300px 300px" }}>
        {[110, 180, 250, 290].map((r, i) => (
          <circle
            key={r}
            cx={CX}
            cy={CY}
            r={r}
            fill="none"
            stroke={color}
            strokeOpacity={0.18 - i * 0.025}
            strokeWidth={0.6}
            strokeDasharray="2 7"
          />
        ))}
      </g>

      {/* Connection lines from center to outer */}
      {outerNodes.map((n, i) => (
        <line
          key={`line-${i}`}
          x1={CX}
          y1={CY}
          x2={n.x}
          y2={n.y}
          stroke={color}
          strokeOpacity={0.22}
          strokeWidth={0.6}
        />
      ))}

      {/* Mid nodes */}
      {midNodes.map((n, i) => (
        <circle
          key={`mid-${i}`}
          cx={n.x}
          cy={n.y}
          r={2.5}
          fill={color}
          opacity={0.55}
          style={{
            transformOrigin: `${n.x}px ${n.y}px`,
            animation: `nvPulse 3s cubic-bezier(0.22,1,0.36,1) ${n.delay}s infinite`,
          }}
        />
      ))}

      {/* Outer nodes */}
      {outerNodes.map((n, i) => (
        <g key={`onode-${i}`}>
          <circle
            cx={n.x}
            cy={n.y}
            r={8}
            fill={color}
            opacity={0.18}
            style={{ animation: `nvOuterPulse 2.8s ease-in-out ${n.delay}s infinite` }}
          />
          <circle
            cx={n.x}
            cy={n.y}
            r={4}
            fill={color}
            opacity={0.95}
          />
        </g>
      ))}

      {/* Center coral pulse */}
      <circle cx={CX} cy={CY} r={48} fill="url(#nv-glow)" />
      <circle
        cx={CX}
        cy={CY}
        r={26}
        fill={accent}
        opacity={0.18}
        style={{ animation: "nvCenterRing 2.6s ease-in-out infinite" }}
      />
      <circle cx={CX} cy={CY} r={9} fill={accent} />

      <style>{`
        .nv-rings { animation: nvRotate 80s linear infinite; }
        @keyframes nvRotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes nvPulse {
          0%, 100% { opacity: 0.35; transform: scale(1); }
          50% { opacity: 0.95; transform: scale(1.6); }
        }
        @keyframes nvOuterPulse {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.5); }
        }
        @keyframes nvCenterRing {
          0%, 100% { opacity: 0.18; transform: scale(1); }
          50% { opacity: 0.05; transform: scale(2.4); }
        }
        @media (prefers-reduced-motion: reduce) {
          .nv-rings, .network-visual circle { animation: none !important; }
        }
        .network-visual circle { transform-origin: center; transform-box: fill-box; }
      `}</style>
    </svg>
  );
};
